import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { MaskLine } from "../site/Motion";
import { PhoneMockup } from "../site/PhoneMockup";
import StoreButtons from "../site/StoreButtons";
import { STATS, APP_SCREENSHOTS } from "../../lib/site";

export const Hero = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yPhone = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-background pt-32 pb-20 lg:pt-40" data-testid="hero-section">
      <div className="grid-lines pointer-events-none absolute inset-0 opacity-60" />
      <motion.div style={{ y: yGlow }} className="mesh-glow pointer-events-none absolute inset-0" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 lg:grid-cols-12 lg:gap-8 lg:px-8">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-brand/15 bg-white px-4 py-1.5 text-xs font-semibold text-brand shadow-sm"
            data-testid="hero-badge"
          >
            <ShieldCheck className="h-4 w-4 text-greenaccent" />
            Renewal &amp; compliance, handled.
          </motion.div>

          <h1 className="mt-6 font-display text-5xl font-800 font-extrabold leading-[0.98] tracking-tighter text-ink sm:text-6xl lg:text-[4.75rem]">
            <MaskLine delay={0.05}>RenewTracker —</MaskLine>
            <MaskLine delay={0.15}>
              Never Miss a Renewal.
            </MaskLine>
            <MaskLine delay={0.25} className="text-brand">
              Never Pay a Penalty.
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            RenewTracker centralises every renewal — vehicles, employees, equipment, licences, permits, contracts and subscriptions — and sends timely email &amp; push reminders so businesses and individuals stay compliant.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.62 }}
            className="mt-8 flex flex-col gap-4"
          >
            <StoreButtons />
            <button
              onClick={() => document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-early-access-btn"
              className="group inline-flex w-fit items-center gap-2 text-sm font-semibold text-ink transition-colors hover:text-brand"
            >
              Join the launch waitlist
              <span className="grid h-8 w-8 place-items-center rounded-full bg-brand text-white transition-transform duration-200 group-hover:translate-x-1">
                <ArrowRight className="h-4 w-4" />
              </span>
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-12 grid grid-cols-2 gap-6 border-t border-slate-200 pt-8 sm:grid-cols-4"
          >
            {STATS.map((s) => (
              <div key={s.label}>
                <p className="font-display text-3xl font-bold text-brand">{s.value}</p>
                <p className="mt-1 text-xs font-medium leading-snug text-slate-500">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            style={{ y: yPhone }}
            initial={{ opacity: 0, scale: 0.92, rotate: 3 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="absolute -inset-6 -z-10 rounded-[3rem] bg-gradient-to-br from-brand/10 via-transparent to-greenaccent/10 blur-2xl" />
            <PhoneMockup image={APP_SCREENSHOTS.dashboard} />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
